/* eslint-disable */
import IdManager from './idManager.js'
import CoreUtils from '../../utils/coreUtils.js';
import { DEBUG_SHOW_NODE_IDS } from './constants.js';

/*****************************************************************************/
export const NODE_TAGS =
{
	SUBNET_INPUTS:		"subnetInputs",
	MAIN_PARAMS:		"mainParams",
	SUBCOMPONENT:		"subcomponent",
}
/*****************************************************************************/
export const NODE_STATE = 
{
	EXPANDED:	0,
	PARTIALLY_COLLAPSED:  1,
}
/*****************************************************************************/
/**
 * A base class for any object in a graph/subnet.
 * */
export default class GraphObject
{
	constructor(pParent)
	{
		this.id = IdManager.getNewId();
		this.name = '';

		this.parent = pParent;
		
		this.vecCenter = new BABYLON.Vector2(0, 0);
		
		this.uiPartBoxes = {};

		this.collapseState = NODE_STATE.EXPANDED;

		this.tags = [];

		this.isDirty = false;
		this.internalDirtyCookCount = -1;

		this.cachedContainingGroupId = -1;
	}

	dispose()
	{

	}

	getIsDirty()
	{
		return this.isDirty;
	}

	getIsLoading()
	{
		return false;
	}

	markClean()
	{
		this.isDirty = false;
	}

	markDirtyInternal(iCookCount)
	{
		this.isDirty = true;
		this.internalDirtyCookCount = iCookCount;
	}

	getIsAlreadyDirtyInternal(iCookCount)
	{
		return iCookCount === this.internalDirtyCookCount && this.isDirty;
	}

	markDirty()
	{
		this.isDirty = true;
		if(this.getParent())
			this.getParent().markDirty();
	}

	markDirtyDelayed()
	{
		this.getGraph().markNodeDirtyAfterGenerate(this.getId());
	}

	addTag(eTag)
	{
		if(!this.hasTag(eTag))
			this.tags.push(eTag);
	}

	getTags()
	{
		return this.tags;
	}

	removeTag(eTag)
	{
		let idx = this.tags.indexOf(eTag);
		if(idx >= 0)
			this.tags.splice(idx, 1);
	}

	hasTag(eTag)
	{
		return this.tags.indexOf(eTag) >= 0;
	}

	getCollapseState()
	{
		return this.collapseState;
	}

	setCollapseState(eValue)
	{
		this.collapseState = eValue;
	}

	setName(strNewName)
	{
		let strOldName = this.name;
		this.name = strNewName;
		if(this.getParent())
			this.getParent().onChildRenamed(this, strOldName, strNewName);
	}

	setParent(pParent)
	{
		this.parent = pParent;
	}

	getGraph()
	{
		for(let pCurrParent = this.getParent(); pCurrParent; pCurrParent = pCurrParent.getParent())
		{
			if(pCurrParent.getGraph)
				return pCurrParent.getGraph();
		}

		return null;
	}

	getParent()
	{
		return this.parent;
	}
	
	getId()
	{
		return this.id;
	}

	setCenter(fX, fY)
	{
		this.vecCenter.x = fX;
		this.vecCenter.y = fY;
	}

	resetUIPartAreas()
	{
		this.uiPartBoxes = {};
	}
	
	getUIPartArea(partId, partType)
	{
		return this.uiPartBoxes[(partType + '') + '_' + partId];	
	}	
	
	setUIPartArea(partId, partType, zIndex, iX, iY, iW, iH)
	{
		this.uiPartBoxes[(partType + '') + '_' + partId] = {x: iX, y: iY, w: iW, h: iH, partId: partId, partType: partType, zIndex: zIndex};
	}

	getNodeUIPart(iX, iY)
	{
		let pCurrInfo = null;
		let iMaxZIndex = -9999;
		for(let iPartId in this.uiPartBoxes)
		{
			let pInfo = this.uiPartBoxes[iPartId];
			if(iX >= pInfo.x && iX <= pInfo.x + pInfo.w && iY >= pInfo.y && iY <= pInfo.y + pInfo.h)
			{
				if(pInfo.zIndex >= iMaxZIndex)
				{
					pCurrInfo = pInfo;
					iMaxZIndex = pInfo.zIndex;
				}
			}
		}
		
		if(pCurrInfo)
			return {...pCurrInfo, node: this};
		else
			return null;
	}

	getCenter()
	{
		return this.vecCenter.clone();
	}

	getName()
	{
		let strRes = '';
		if(this.name && this.name.length > 0)
			strRes = this.name;
		else
			strRes = CoreUtils.capitalizeFirstLetterAllWords(this.getType());
		return strRes;
	}

	getLabel()
	{ 
		let strRes = this.getName();
		if(DEBUG_SHOW_NODE_IDS)
			strRes = '[' + this.getId() + ']' + strRes;
		return strRes;
	}

	resetCachedGroupId()
	{
		this.cachedContainingGroupId = -1;
	}

	getContainingGroup()
	{
		if(this.cachedContainingGroupId === 0)
			return null;

		let pSubnet = this.getParent();
		if(this.cachedContainingGroupId > 0)
			return pSubnet.getNode(this.cachedContainingGroupId);
		
		let pNode;
		let pNodesMap = pSubnet.getNodes();
		for(let iNodeId in pNodesMap)
		{
			if(iNodeId == this.getId())
				continue;

			pNode = pNodesMap[iNodeId];
			if(pNode.doesContainObject && pNode.doesContainObject(this.getId()))
			{
				this.cachedContainingGroupId = pNode.getId();
				return pNode;
			}
		}

		this.cachedContainingGroupId = 0;
		return null;
	}

	saveToObject()
	{
		let pRes = {};
		
		pRes['id'] = this.id;
		if(this.name.length > 0)
			pRes['n'] = this.name;
		pRes['c'] = { x: parseFloat(this.vecCenter.x.toFixed(4)), y: parseFloat(this.vecCenter.y.toFixed(4))};
		if(this.collapseState != NODE_STATE.EXPANDED)
			pRes['cs'] = this.collapseState;

		if(this.tags.length > 0)
			pRes['tags'] = this.tags.slice();

		return pRes;
	}

	static loadFromObject(pObj, pTarget)
	{
		pTarget.id = pObj['id'];

		if(pObj['n'])
			pTarget.name = pObj['n'];
		else
			pTarget.name = '';

		pTarget.vecCenter.x = pObj['c'].x;
		pTarget.vecCenter.y = pObj['c'].y;

		if(pObj['cs'] !== undefined)
			pTarget.collapseState = pObj['cs'];

		if(pObj['tags'])
			pTarget.tags = pObj['tags'];
	}

	getReferencedAssetIds(resMapOut)
	{
		// Nothing to do here
	}

	getMaxId()
	{
		return this.getId();
	}
}
/*****************************************************************************/